import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * CustomFieldRenderer
 *
 * Props:
 * module      {string}   - e.g. 'crm', 'projects', 'tasks'
 * customData  {object}   - existing saved JSON values, e.g. { "Lead Source": "Website" }
 * onChange    {function} - called with the full updated customData object on every change
 * definitions {array}    - optional: pass pre-fetched field definitions to skip the internal fetch
 * compact     {boolean}  - optional: single-column layout for tight spaces (default: false)
 */
export default function CustomFieldRenderer({
  module,
  customData = {},
  onChange,
  definitions: externalDefinitions,
  compact = false,
}) {
  const [definitions, setDefinitions] = useState(externalDefinitions || []);
  const [loading, setLoading] = useState(!externalDefinitions);
  const [values, setValues] = useState(customData);

  // ── Sync external customData into local state ─────────────────────────────
  useEffect(() => {
    setValues(customData);
  }, [JSON.stringify(customData)]); // eslint-disable-line

// ── Fetch definitions if not passed as a prop ─────────────────────────────
  const fetchDefinitions = useCallback(async () => {
    if (externalDefinitions) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/custom-fields?module=${module}`);
      setDefinitions(data);
    } catch (error) {
      console.error("Renderer Fetch Error:", error);
      // Demo fallback so the component is usable without a backend
      const MOCK = {
        crm: [
          { id: 1, field_label: 'Lead Source',          field_type: 'select',   options: ['Website','Referral','Cold Call','Exhibition'], is_required: true },
          { id: 2, field_label: 'Expected Close Date',  field_type: 'date',     options: [], is_required: false },
          { id: 3, field_label: 'Budget Range',         field_type: 'text',     options: [], is_required: false },
          { id: 4, field_label: 'Notes / Remarks',      field_type: 'textarea', options: [], is_required: false },
          { id: 5, field_label: 'Interested in RCC?',  field_type: 'checkbox', options: [], is_required: false },
        ],
        projects: [
          { id: 6, field_label: 'Site Engineer',        field_type: 'text',     options: [], is_required: false },
          { id: 7, field_label: 'Approval Date',        field_type: 'date',     options: [], is_required: false },
          { id: 8, field_label: 'Risk Level',           field_type: 'select',   options: ['Low','Medium','High'], is_required: false },
        ],
        tasks: [
          { id: 9,  field_label: 'Priority Tag',        field_type: 'select',   options: ['Blocker','Critical','Normal','Minor'], is_required: false },
          { id: 10, field_label: 'Estimated Hours',     field_type: 'number',   options: [], is_required: false },
        ],
      };
      setDefinitions(MOCK[module] || []);
    } finally {
      setLoading(false);
    }
  }, [module, externalDefinitions]);

  useEffect(() => { fetchDefinitions(); }, [fetchDefinitions]);

  // ── Handle input change ───────────────────────────────────────────────────
  const handleChange = (fieldLabel, value) => {
    const updated = { ...values, [fieldLabel]: value };
    setValues(updated);
    if (onChange) onChange(updated);
  };

  // ── Render a single field based on its type ───────────────────────────────
  const renderField = (field) => {
    const baseInput = 'w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-sm text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm';
    const currentVal = values[field.field_label] ?? '';

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={currentVal}
            onChange={e => handleChange(field.field_label, e.target.value)}
            placeholder={`Enter ${field.field_label.replace(/0$/, '').toLowerCase()}…`}
            required={field.is_required}
            className={baseInput}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={currentVal}
            onChange={e => handleChange(field.field_label, e.target.value)}
            placeholder={`Enter ${field.field_label.replace(/0$/, '').toLowerCase()}…`}
            required={field.is_required}
            rows={3}
            className={`${baseInput} resize-none`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={currentVal}
            onChange={e => handleChange(field.field_label, e.target.value)}
            placeholder="0"
            required={field.is_required}
            className={baseInput}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={currentVal}
            onChange={e => handleChange(field.field_label, e.target.value)}
            required={field.is_required}
            className={`${baseInput} dark:[color-scheme:dark]`}
          />
        );

      case 'select':
        return (
          <select
            value={currentVal}
            onChange={e => handleChange(field.field_label, e.target.value)}
            required={field.is_required}
            className={baseInput}
          >
            <option value="">— Select —</option>
            {(Array.isArray(field.options) ? field.options : []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => handleChange(field.field_label, !currentVal)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                currentVal ? 'bg-blue-600' : 'bg-gray-300 dark:bg-[#30363d]'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                currentVal ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
            <span className="text-sm font-medium text-gray-600 dark:text-slate-300">{currentVal ? 'Yes' : 'No'}</span>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={currentVal}
            onChange={e => handleChange(field.field_label, e.target.value)}
            className={baseInput}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-4 w-full">
        <ArrowPathIcon className="w-4 h-4 animate-spin" />
        Loading custom fields…
      </div>
    );
  }

  if (definitions.length === 0) return null;

  return (
    // 🟢 NEW: Completely stripped wrapper. Just the grid for the fields.
    <div className={`grid gap-4 w-full ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
      {definitions.map(field => (
        <div
          key={field.id}
          className={
            (field.field_type === 'textarea' || field.field_type === 'checkbox') && !compact
              ? 'md:col-span-2'
              : ''
          }
        >
          <label className="block text-xs font-semibold text-gray-500 dark:text-slate-300 uppercase tracking-wide mb-1.5">
            {/* 🟢 NEW: Safely removes the trailing '0' if it accidentally comes from your database */}
            {field.field_label.replace(/0$/, '')}
            {field.is_required && <span className="text-red-400 ml-0.5">*</span>}
          </label>
          {renderField(field)}
        </div>
      ))}
    </div>
  );
}